// Tema claro/oscuro de TODA la plataforma nueva (en la de siempre sólo existe claro). Se recuerda por navegador.
//
// Cómo se cambia sin trabar la página (medido; ver docs/plataforma-nueva/aprendizajes/shell.md § Tema):
//   1. CSS primero: `data-theme` se escribe en <html> en el mismo clic. Todo lo que es CSS (tokens, vidrio,
//      textos) pinta el tema nuevo en el siguiente cuadro, sin esperar a React.
//   2. Sin transiciones durante el cambio: si no, cada control anima su color 350 ms y el navegador repinta el
//      vidrio cuadro por cuadro. El interruptor (.dbm-tt) conserva la suya (sol ⇄ luna).
//   3. JS después: el estado de React (lo leen las gráficas, que ECharts no lee CSS) cambia en una transición,
//      sin bloquear el clic.
//   4. El estado vive en <TemaGlobal>: al cambiar sólo se vuelven a pintar los que leen el tema (useDbTheme),
//      no la plataforma completa.
import { startTransition, useCallback, useEffect, useState } from 'react';
import { DbThemeProvider } from '../../ds/theme/ThemeProvider';
import { prepararGraficos } from './graficos';

const LLAVE = 'db-plataforma:tema';

function leer() {
  try {
    const guardado = localStorage.getItem(LLAVE);
    if (guardado === 'dark' || guardado === 'light') return guardado;
  } catch { /* sin almacenamiento: claro */ }
  return 'light';
}

/** Escribe el tema en <html> de inmediato (paso 1 y 2). Idempotente. */
export function aplicarTema(tema) {
  const html = document.documentElement;
  if (html.getAttribute('data-theme') === tema) return;
  const pausa = document.createElement('style');
  pausa.setAttribute('data-db-tema', '');
  pausa.textContent = '*:not(.dbm-tt *),*:not(.dbm-tt *)::before,*:not(.dbm-tt *)::after{transition:none!important}';
  document.head.appendChild(pausa);
  html.setAttribute('data-theme', tema);
  // Dos cuadros: el primero recalcula estilos ya sin transiciones; en el segundo se pueden volver a usar.
  requestAnimationFrame(() => requestAnimationFrame(() => pausa.remove()));
}

export function useTemaPersistido() {
  const [tema, setTema] = useState(() => {
    const inicial = leer();
    // Antes del primer cuadro: sin parpadeo claro → oscuro al entrar con el tema oscuro guardado.
    if (typeof document !== 'undefined') document.documentElement.setAttribute('data-theme', inicial);
    return inicial;
  });
  const cambiar = useCallback((nuevo) => {
    aplicarTema(nuevo);
    startTransition(() => setTema(nuevo));
    try { localStorage.setItem(LLAVE, nuevo); } catch { /* sólo esta sesión */ }
  }, []);
  return [tema, cambiar];
}

/** Proveedor del tema global (scope root). Sus hijos llegan ya creados: cambiar el tema no los vuelve a
 *  pintar, sólo a quienes leen useDbTheme(). */
export function TemaGlobal({ children }) {
  const [tema, cambiar] = useTemaPersistido();
  // ¿Hay GPU? (app/graficos.js) — desde el arranque, también para la pantalla de acceso.
  useEffect(() => { prepararGraficos(); }, []);
  return (
    <DbThemeProvider scope="root" theme={tema} onThemeChange={cambiar}>
      {children}
    </DbThemeProvider>
  );
}
